// features/orders/components/QuickSaleModal.jsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '../../../components/ui/Modal'
import { Button } from '../../../components/ui/Button'
import { useAutoSave } from '../../../hooks/useAutoSave'
import { toast, toastSuccess, toastError } from '../../../components/feedback/Toast'
import { apiFetch } from '../../../api'
import { cn } from '../../../lib/cn'
import { Check } from 'lucide-react'

const STEPS = [
  { id: 'customer', label: 'Client' },
  { id: 'products', label: 'Produits' },
  { id: 'payment', label: 'Paiement' },
]

const DRAFT_KEY = 'quick-sale-draft'

export function QuickSaleModal({ open, onClose, onSuccess }) {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState({
    customer: null,
    lines: [],
    payment: { mode: 'cash' },
  })
  const [loading, setLoading] = useState(false)

  // Auto-save draft (localStorage)
  useAutoSave(
    draft,
    (value) => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(value))
    },
    500
  )

  // Restore draft on mount
  useEffect(() => {
    if (!open) return

    const saved = localStorage.getItem(DRAFT_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Vérifier si c'est récent (< 24h)
        const age = Date.now() - (parsed.timestamp || 0)
        if (age < 24 * 60 * 60 * 1000) {
          const shouldRestore = window.confirm('Reprendre la vente en cours ?')
          if (shouldRestore) {
            setDraft(parsed)
          } else {
            localStorage.removeItem(DRAFT_KEY)
          }
        } else {
          localStorage.removeItem(DRAFT_KEY)
        }
      } catch {
        localStorage.removeItem(DRAFT_KEY)
      }
    }
  }, [open])

  async function handleSubmit() {
    setLoading(true)

    try {
      const result = await apiFetch('/api/orders', {
        method: 'POST',
        body: draft,
      })

      toastSuccess('Vente créée !', `Facture ${result.external_id}`, {
        action: {
          label: 'Voir',
          onClick: () => navigate(`/orders/${result.id}`),
        },
      })

      localStorage.removeItem(DRAFT_KEY)
      onSuccess?.()
      onClose()

      // Reset
      setDraft({
        customer: null,
        lines: [],
        payment: { mode: 'cash' },
      })
      setStep(0)
    } catch (err) {
      toastError('Erreur création', err.message)
    } finally {
      setLoading(false)
    }
  }

  function canContinue() {
    if (step === 0) return !!draft.customer
    if (step === 1) return draft.lines.length > 0
    if (step === 2) return !!draft.payment?.mode
    return false
  }

  function handleNext() {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1)
    } else {
      handleSubmit()
    }
  }

  function handlePrevious() {
    if (step > 0) {
      setStep(s => s - 1)
    } else {
      onClose()
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="xl" showCloseButton={false}>
      <div className="min-h-[500px] flex flex-col">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg transition',
                  i < step && 'bg-green-50',
                  i === step && 'bg-purple-50 ring-2 ring-purple-200',
                  i > step && 'bg-slate-50'
                )}
              >
                <div
                  className={cn(
                    'h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold',
                    i < step && 'bg-green-600 text-white',
                    i === step && 'bg-purple-600 text-white',
                    i > step && 'bg-slate-200 text-slate-500'
                  )}
                >
                  {i < step ? <Check size={16} /> : i + 1}
                </div>
                <span className={cn(
                  'text-sm font-semibold',
                  i <= step ? 'text-slate-900' : 'text-slate-500'
                )}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={cn(
                  'w-8 h-1 rounded-full transition',
                  i < step ? 'bg-green-600' : 'bg-slate-200'
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1">
          {step === 0 && <StepCustomer draft={draft} onChange={setDraft} />}
          {step === 1 && <StepProducts draft={draft} onChange={setDraft} />}
          {step === 2 && <StepPayment draft={draft} onChange={setDraft} />}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-8 pt-6 border-t border-slate-200">
          <Button variant="secondary" onClick={handlePrevious} className="flex-1">
            {step === 0 ? 'Annuler' : 'Précédent'}
          </Button>

          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!canContinue()}
            loading={loading}
            className="flex-1"
          >
            {step === STEPS.length - 1 ? 'Créer la vente' : 'Suivant'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// Placeholders pour les steps (à implémenter)
function StepCustomer({ draft, onChange }) {
  return (
    <div className="p-6 border border-slate-200 rounded-lg bg-slate-50">
      <p className="text-sm text-slate-600">
        Step Customer - À implémenter (recherche client + création rapide)
      </p>
      <Button
        size="sm"
        variant="accent"
        onClick={() => onChange({ ...draft, customer: { id: 1, name: 'Client Test' } })}
        className="mt-4"
      >
        Sélectionner un client (demo)
      </Button>
    </div>
  )
}

function StepProducts({ draft, onChange }) {
  return (
    <div className="p-6 border border-slate-200 rounded-lg bg-slate-50">
      <p className="text-sm text-slate-600">
        Step Products - À implémenter (sélection produits + quantités)
      </p>
      <Button
        size="sm"
        variant="accent"
        onClick={() => onChange({ ...draft, lines: [{ product_id: 1, qty: 1, unit_price: 10000 }] })}
        className="mt-4"
      >
        Ajouter un produit (demo)
      </Button>
    </div>
  )
}

function StepPayment({ draft, onChange }) {
  return (
    <div className="p-6 border border-slate-200 rounded-lg bg-slate-50">
      <p className="text-sm text-slate-600">
        Step Payment - À implémenter (mode paiement + montants)
      </p>
      <div className="mt-4 flex gap-2">
        {['cash', 'credit', 'installment'].map(mode => (
          <Button
            key={mode}
            size="sm"
            variant={draft.payment?.mode === mode ? 'primary' : 'secondary'}
            onClick={() => onChange({ ...draft, payment: { mode } })}
          >
            {mode}
          </Button>
        ))}
      </div>
    </div>
  )
}
